import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { getAdminFamilyClans } from "../store/familyClan/action";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";

const numberFormat = new Intl.NumberFormat();
const rangeFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const PERIOD_OPTIONS = [
  { value: "week", label: "This week" },
  { value: "lastWeek", label: "Last week" },
  { value: "month", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const DEFAULT_PERIOD = "week";

// The table holds the whole clan list in memory, so these numeric columns sort
// client side. Clicking cycles descending → ascending → back to the API order
// (newest clan first).
const NO_SORT = { key: null, direction: "desc" };

const SortableHeader = ({ label, sortKey, sort, onSort }) => {
  const active = sort.key === sortKey;
  const icon = !active
    ? "fa-sort"
    : sort.direction === "asc"
      ? "fa-sort-up"
      : "fa-sort-down";

  return (
    <th
      className={`family-clans-sortable${active ? " is-sorted" : ""}`}
      aria-sort={
        active
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button type="button" onClick={() => onSort(sortKey)}>
        <span>{label}</span>
        <i className={`fas ${icon}`} aria-hidden="true"></i>
      </button>
    </th>
  );
};

const formatRangeDate = (value) =>
  value ? rangeFormat.format(new Date(value)) : null;

// The API's `endDate` is the exclusive upper bound, so step back a millisecond
// to name the last day the window actually covers.
const formatRangeEndDate = (value) =>
  value ? rangeFormat.format(new Date(new Date(value).getTime() - 1)) : null;

// The API returns the resolved window; `endDate` is open ended for the periods
// that run up to now (this week, this month, all time).
const describeRange = (period) => {
  if (!period) return null;
  const start = formatRangeDate(period.startDate);
  const end = formatRangeEndDate(period.endDate);

  if (!start && !end) return "all time";
  if (start && !end) return `${start} – today`;
  if (!start && end) return `Up to ${end}`;
  return `${start} – ${end}`;
};

const FamilyClans = ({ getAdminFamilyClans }) => {
  const { familyClans, familyClansPeriod, loading, error } = useSelector(
    (state) => state.familyClan,
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState(NO_SORT);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [periodError, setPeriodError] = useState("");
  // Only an applied selection reaches the API, so editing the custom dates does
  // not refetch on every keystroke.
  const [appliedPeriod, setAppliedPeriod] = useState({
    period: DEFAULT_PERIOD,
  });

  useEffect(() => {
    getAdminFamilyClans(appliedPeriod);
  }, [getAdminFamilyClans, appliedPeriod]);

  const filteredClans = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return familyClans;
    return familyClans.filter(
      (clan) =>
        clan.name?.toLowerCase().includes(term) ||
        clan.clanId?.toLowerCase().includes(term) ||
        clan.level?.name?.toLowerCase().includes(term),
    );
  }, [familyClans, search]);

  const sortedClans = useMemo(() => {
    if (!sort.key) return filteredClans;
    const direction = sort.direction === "asc" ? 1 : -1;
    return [...filteredClans].sort(
      (a, b) =>
        ((Number(a[sort.key]) || 0) - (Number(b[sort.key]) || 0)) * direction,
    );
  }, [filteredClans, sort]);

  const handleSort = (key) =>
    setSort((current) => {
      if (current.key !== key) return { key, direction: "desc" };
      if (current.direction === "desc") return { key, direction: "asc" };
      return NO_SORT;
    });

  useEffect(() => setPage(0), [search, rowsPerPage, sort]);

  const visibleClans =
    rowsPerPage === -1
      ? sortedClans
      : sortedClans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const pageCount =
    rowsPerPage === -1 ? 1 : Math.ceil(sortedClans.length / rowsPerPage);
  const firstEntry = sortedClans.length
    ? rowsPerPage === -1
      ? 1
      : page * rowsPerPage + 1
    : 0;
  const lastEntry =
    rowsPerPage === -1
      ? sortedClans.length
      : Math.min((page + 1) * rowsPerPage, sortedClans.length);

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    setPeriod(value);
    setPeriodError("");

    if (value !== "custom") {
      setAppliedPeriod({ period: value });
    }
  };

  const handleCustomRangeSubmit = (event) => {
    event.preventDefault();

    if (!customStartDate || !customEndDate) {
      setPeriodError("Choose both a start date and an end date.");
      return;
    }

    if (customStartDate > customEndDate) {
      setPeriodError("The start date cannot be after the end date.");
      return;
    }

    setPeriodError("");
    setAppliedPeriod({
      startDate: customStartDate,
      endDate: customEndDate,
    });
  };

  const appliedRangeLabel = describeRange(familyClansPeriod);

  return (
    <section className="family-clans-page" aria-labelledby="family-clans-title">
      <div className="page-title">
        <div className="row align-items-end">
          <div className="col-12 col-md-7">
            <h3 id="family-clans-title" className="mb-2 text-white">
              Family Clans
            </h3>
          </div>
          <div className="col-12 col-md-5">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-md-end"
            >
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Family Clans
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card family-clans-card">
        <div className="card-header family-clans-toolbar">
          <div>
            <span className="family-clans-count">
              {numberFormat.format(familyClans.length)}
            </span>
            <span className="family-clans-count-label"> active clans</span>
          </div>
          <div className="family-clans-toolbar-controls">
            <label className="family-clans-search">
              <span className="sr-only">Search family clans</span>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                value={search}
                placeholder="Search name, clan ID, or level"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label className="family-clans-period">
              <span>Cashout/Recharge Period</span>
              <select
                className="form-control"
                value={period}
                onChange={handlePeriodChange}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {period === "custom" && (
          <form
            className="family-clan-roster-filters"
            onSubmit={handleCustomRangeSubmit}
          >
            <label>
              <span>Start date</span>
              <input
                type="date"
                className="form-control"
                value={customStartDate}
                onChange={(event) => setCustomStartDate(event.target.value)}
                max={customEndDate || undefined}
              />
            </label>
            <label>
              <span>End date</span>
              <input
                type="date"
                className="form-control"
                value={customEndDate}
                onChange={(event) => setCustomEndDate(event.target.value)}
                min={customStartDate || undefined}
              />
            </label>
            <div className="family-clan-roster-filter-actions">
              <button type="submit" className="btn btn-sm btn-primary">
                Show cashout/recharge
              </button>
            </div>
          </form>
        )}

        {(appliedRangeLabel || periodError) && (
          <div className="family-clans-period-summary">
            {periodError ? (
              <span className="text-danger">{periodError}</span>
            ) : (
              <span>
                VCoin cashout and diamond recharge for {appliedRangeLabel}
              </span>
            )}
          </div>
        )}

        <div className="card-body card-overflow p-0">
          <div className="table-responsive">
            <table className="table family-clans-table mb-0">
              <thead>
                <tr>
                  <th>Clan</th>
                  <SortableHeader
                    label="VCoin Cashout"
                    sortKey="vcoinCashout"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Diamond Recharge"
                    sortKey="diamondRecharge"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Members"
                    sortKey="memberCount"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <th>Clan ID</th>
                  <SortableHeader
                    label="EXP"
                    sortKey="exp"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <th>Level</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="8" className="family-clans-state">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading family clans…
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan="8" className="family-clans-state text-danger">
                      {error}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  visibleClans.map((clan) => (
                    <tr key={clan._id}>
                      <td>
                        <div className="family-clan-identity">
                          <img
                            src={clan.profilePicture || noImage}
                            onError={(event) => {
                              event.currentTarget.src = noImage;
                            }}
                            alt=""
                          />
                          <strong>{clan.name || "Unnamed clan"}</strong>
                        </div>
                      </td>
                      <td>{numberFormat.format(clan.vcoinCashout || 0)}</td>
                      <td>{numberFormat.format(clan.diamondRecharge || 0)}</td>
                      <td>
                        <span className="family-clans-stat">
                          <i
                            className="fas fa-user-friends "
                            aria-hidden="true"
                          ></i>
                          {numberFormat.format(clan.memberCount || 0)}
                        </span>
                      </td>
                      <td>
                        <code className="blue-primary">
                          {clan.clanId || "—"}
                        </code>
                      </td>
                      <td>{numberFormat.format(clan.exp || 0)}</td>
                      <td>
                        <div className="family-clan-level">
                          <img
                            src={clan.level?.image || noImage}
                            onError={(event) => {
                              event.currentTarget.src = noImage;
                            }}
                            alt=""
                          />
                          <span>{clan.level?.name || "Unranked"}</span>
                        </div>
                      </td>
                      <td className="text-end">
                        <Link
                          to={`/admin/familyClans/${clan._id}`}
                          className="btn btn-sm family-clans-info"
                          aria-label={`View details for ${clan.name}`}
                        >
                          <i
                            className="fas fa-info-circle"
                            aria-hidden="true"
                          ></i>
                          <span>Info</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                {!loading && !error && !visibleClans.length && (
                  <tr>
                    <td colSpan="8" className="family-clans-state">
                      No active family clans found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !error && sortedClans.length > 0 && (
          <div className="card-footer family-clans-pagination">
            <span>
              Showing {firstEntry}–{lastEntry} of {sortedClans.length}
            </span>
            <div className="family-clans-page-controls">
              <label>
                Rows{" "}
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(event) =>
                    setRowsPerPage(Number(event.target.value))
                  }
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={-1}>All</option>
                </select>
              </label>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0}
                onClick={() => setPage((value) => value - 1)}
                aria-label="Previous page"
              >
                &lsaquo;
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((value) => value + 1)}
                aria-label="Next page"
              >
                &rsaquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default connect(null, { getAdminFamilyClans })(FamilyClans);
